import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { FaArrowLeft, FaFile, FaUsers, FaShieldAlt, FaChartBar, FaFileContract, FaLightbulb, FaQuestionCircle } from 'react-icons/fa';
import { useLanguage } from '../../i18n';
import FileManager from '../../components/adminpanel_comp/FileManager';
import UserManager from '../../components/adminpanel_comp/UserManager';
import PlanManager from '../../components/adminpanel_comp/PlanManager';
import TyC from '../../components/tyc_comp/tyc';
import Guia from '../../components/guia/guia';
import FAQ from '../../components/faq/faq';
import useActivityTracker from '../../hooks/useActivityTracker';
import './AdminPage.css';

const STRANDS_API_URL = process.env.REACT_APP_STRANDS_API_URL || 'http://localhost:5001';

const AdminPage = ({ userFeatures = {} }) => {
  const { user } = useAuthenticator((context) => [context.user]);
  const navigate = useNavigate();
  const { t } = useLanguage();
  useActivityTracker(user?.username, 'admin');

  const [activeTab, setActiveTab] = useState('users');
  const [userConfig, setUserConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  // Verificar permisos de admin
  useEffect(() => {
    const checkAdminAccess = async () => {
      if (!user) {
        navigate('/');
        return;
      }

      try {
        const response = await fetch(
          `${STRANDS_API_URL}/api/user/config?username=${encodeURIComponent(user.username)}`
        );

        if (response.ok) {
          const data = await response.json();

          if (data.success) {
            setUserConfig(data);

            // Verificar rol de admin
            if (data.role?.toLowerCase() !== 'admin') {
              setAccessDenied(true);
            }
          } else {
            setAccessDenied(true);
          }
        } else {
          setAccessDenied(true);
        }
      } catch (error) {
        console.error('[AdminPage] Error checking access:', error);
        setAccessDenied(true);
      } finally {
        setIsLoading(false);
      }
    };

    checkAdminAccess();
  }, [user, navigate]);

  if (isLoading) {
    return (
      <div className="admin-page">
        <div className="admin-loading">
          <div className="loading-spinner"></div>
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="admin-page">
        <div className="access-denied">
          <FaShieldAlt className="denied-icon" />
          <h2>{t('errors.unauthorized')}</h2>
          <p>{t('errors.unauthorized')}</p>
          <button className="back-btn" onClick={() => navigate('/')}>
            <FaArrowLeft /> {t('common.back')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      {/* Header */}
      <header className="admin-header">
        <button className="back-btn" onClick={() => navigate('/')}>
          <FaArrowLeft /> {t('admin.back')}
        </button>
        <div className="header-info">
          <h1>{t('admin.title')}</h1>
        </div>
        <div className="header-spacer"></div>
      </header>

      {/* Navegacion por tabs */}
      <nav className="admin-tabs">
        <button
          className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          <FaUsers /> {t('admin.users')}
        </button>
        {userFeatures.fileUpload !== false && (
        <button
          className={`tab-btn ${activeTab === 'files' ? 'active' : ''}`}
          onClick={() => setActiveTab('files')}
        >
          <FaFile /> {t('admin.files')}
        </button>
        )}
        {userFeatures.chatAgent !== false && (
        <button
          className={`tab-btn ${activeTab === 'guide' ? 'active' : ''}`}
          onClick={() => setActiveTab('guide')}
        >
          <FaLightbulb /> {t('admin.guide')}
        </button>
        )}
        {userFeatures.chatAgent !== false && (
        <button
          className={`tab-btn ${activeTab === 'plan' ? 'active' : ''}`}
          onClick={() => setActiveTab('plan')}
        >
          <FaChartBar /> {t('admin.plan')}
        </button>
        )}
        <button
          className={`tab-btn ${activeTab === 'terms' ? 'active' : ''}`}
          onClick={() => setActiveTab('terms')}
        >
          <FaFileContract /> {t('admin.terms')}
        </button>
        <button
          className={`tab-btn ${activeTab === 'faq' ? 'active' : ''}`}
          onClick={() => setActiveTab('faq')}
        >
          <FaQuestionCircle /> {t('admin.faq')}
        </button>
      </nav>

      {/* Contenido */}
      <main className="admin-content">
        {activeTab === 'users' && (
          <UserManager username={user?.username} />
        )}
        {activeTab === 'files' && (
          <FileManager username={user?.username} />
        )}
        {activeTab === 'plan' && (
          <PlanManager username={user?.username} />
        )}
        {activeTab === 'terms' && (
          <div className="admin-panel-section">
            <TyC />
          </div>
        )}
        {activeTab === 'guide' && (
          <div className="admin-guia-embedded">
            <Guia />
          </div>
        )}
        {activeTab === 'faq' && (
          <div className="admin-panel-section">
            <FAQ />
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPage;
